import React from 'react';
import {
  Alert,
  Container,
  DatePicker,
  ExpandableSection,
  FormField,
  Header,
  Input,
  Select,
  SpaceBetween,
  TextContent,
} from '@amzn/awsui-components-react-v3';
import { Link } from 'src/components/common/Link';
import { validateTicketUrl } from 'src/commons/validationUtils';
import { TemplateMetadata } from 'src/components/common/checkout/common';

export interface TemplateRequestDetailsContainerProps {
  additionalMetadata: TemplateMetadata[];
  setAdditionalMetadata: (additionalMetadata: TemplateMetadata[]) => void;
}

export const shouldEnableSimContainer = (templates: TemplateMetadata[]) => {
  let shouldEnable = false;
  templates?.forEach((item) => {
    if (item.accessManagementInfo?.simTemplates?.[0]) {
      shouldEnable = true;
    }
  });
  return shouldEnable;
};

export const shouldEnableApprovalContainer = (templates: TemplateMetadata[]) => {
  return templates?.some(
    (item) =>
      item.accessManagementInfo?.approvalsWorkflowTemplate?.approvalTemplateId ||
      item.accessRequestWorkflows?.[0]?.approvalsWorkflowTemplate?.approvalTemplateId,
  );
};

export const TemplateRequestDetailsContainer = (props: TemplateRequestDetailsContainerProps) => {
  let validateNeededOnDate = (neededOnDate: string) => {
    if (!neededOnDate) {
      return true;
    }
    // ensure the date is in a valid format
    const regex = /^[0-9]{4}-[0-9]{2}-[0-9]{2}$/;
    if (!regex.test(neededOnDate)) {
      return false;
    }
    // ensure the date is after today
    const date = new Date(neededOnDate);
    const today = new Date();
    const todayUTC = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate()));
    return date > todayUTC;
  };

  const convertValueToLabel = (value: string) => {
    const mapping = {
      LOW: 'Low',
      MEDIUM: 'Medium',
      HIGH: 'High',
    };
    return mapping[value];
  };

  const getUniqueApprovalTemplateIds = (additionalMetadata: TemplateMetadata[]) => {
    let uniqueIds = new Set<string>();
    let uniqueAMIS = [];
    additionalMetadata.forEach((aMIS) => {
      const amiApprovalId = aMIS.accessManagementInfo?.approvalsWorkflowTemplate?.approvalTemplateId;
      const arwApprovalId = aMIS.accessRequestWorkflows?.[0]?.approvalsWorkflowTemplate?.approvalTemplateId;
      if (!!amiApprovalId && !uniqueIds.has(amiApprovalId)) {
        uniqueAMIS.push(aMIS);
        uniqueIds.add(aMIS.accessManagementInfo?.approvalsWorkflowTemplate?.approvalTemplateId);
      }
      if (!!arwApprovalId && !uniqueIds.has(arwApprovalId)) {
        uniqueAMIS.push(aMIS);
        uniqueIds.add(aMIS.accessRequestWorkflows?.[0]?.approvalsWorkflowTemplate?.approvalTemplateId);
      }
    });
    return uniqueAMIS;
  };
  const shouldEnableSIM = shouldEnableSimContainer(props.additionalMetadata);
  const shouldEnableApprovals = shouldEnableApprovalContainer(props.additionalMetadata);
  return (
    <SpaceBetween size='m' direction={'vertical'}>
      {!shouldEnableSIM && !shouldEnableApprovals && (
        <Alert header={'No Amazon Approvals or ticket workflows'} type={'success'}>
          {'Amazon approvals or tickets are not required for these datasets.'}
        </Alert>
      )}
      {shouldEnableSIM && (
        <Container
          header={
            <Header variant='h3' description='The data owner requests additional information.'>
              Additional request details
            </Header>
          }
        >
          {props.additionalMetadata.map((template) => {
            if (!template.accessManagementInfo?.simTemplates?.[0]) {
              return <></>;
            }
            const title = `${template.resourceId}`;
            return (
              <ExpandableSection
                defaultExpanded
                key={template.templateId}
                header={<React.Fragment>{title}</React.Fragment>}
              >
                {template.accessManagementInfo?.simTemplates?.map((simTemplate, ind) => (
                  <SpaceBetween size={'m'} direction={'vertical'}>
                    <TextContent>
                      <p>Create a ticket using the following SIM template, then paste the link here.</p>
                      <p>
                        <b>SIM template: </b>
                        <Link href={simTemplate.simTemplateLink} external={true}>
                          {simTemplate.simTemplateName}
                        </Link>
                      </p>
                    </TextContent>
                    <FormField label={'Ticket URL'}>
                      <Input
                        name='Access request form/s'
                        placeholder='https://t.corp.amazon.com/123456789'
                        ariaRequired={true}
                        invalid={validateTicketUrl(simTemplate.simTicketLink) == false}
                        value={simTemplate.simTicketLink}
                        onChange={(e) => {
                          let newAdditionalMetadata = JSON.parse(
                            JSON.stringify(props.additionalMetadata),
                          ) as TemplateMetadata[];
                          newAdditionalMetadata.forEach((t) => {
                            if (t.templateId == template.templateId) {
                              t.accessManagementInfo.simTemplates.find(
                                (st) => st.simTemplateLink == simTemplate.simTemplateLink,
                              ).simTicketLink = e.detail.value;
                            }
                          });
                          props.setAdditionalMetadata(newAdditionalMetadata);
                        }}
                      />
                    </FormField>
                  </SpaceBetween>
                ))}
              </ExpandableSection>
            );
          })}
        </Container>
      )}
      {shouldEnableApprovals && (
        <Container
          header={
            <Header
              variant='h3'
              description='When the request is raised, an Amazon Approvals workflow will be created from this template.
              You can view the status of your request at any time by going to the "Access requests" page.'
            >
              Approval workflow details
            </Header>
          }
        >
          {getUniqueApprovalTemplateIds(props.additionalMetadata).map((aMIS: TemplateMetadata) => {
            if (
              !(
                aMIS.accessManagementInfo?.approvalsWorkflowTemplate ||
                aMIS.accessRequestWorkflows?.[0]?.approvalsWorkflowTemplate
              ) ||
              !(
                aMIS.accessManagementInfo?.approvalsWorkflowTemplate?.approvalTemplateId ||
                aMIS.accessRequestWorkflows?.[0]?.approvalsWorkflowTemplate.approvalTemplateId
              )
            ) {
              return <></>;
            }
            const title = `${aMIS.id}`;
            const isAMType: boolean =
              aMIS.accessManagementInfo?.approvalsWorkflowTemplate?.approvalTemplateId != undefined;
            const approvalsWorkflow = isAMType
              ? aMIS.accessManagementInfo?.approvalsWorkflowTemplate
              : aMIS.accessRequestWorkflows?.[0]?.approvalsWorkflowTemplate;
            return (
              <ExpandableSection
                defaultExpanded
                key={aMIS.templateId}
                header={<React.Fragment>{title}</React.Fragment>}
              >
                <SpaceBetween size={'m'} direction={'vertical'}>
                  <FormField label={'Approval template ID'}>
                    <Input
                      name='Approval template ID'
                      ariaRequired={true}
                      value={approvalsWorkflow.approvalTemplateId}
                      disabled={true}
                    />
                  </FormField>
                  <FormField label={'Omni template'}>
                    <Input
                      name='Omni template ID'
                      ariaRequired={true}
                      value={`${aMIS.templateType} (${aMIS.templateId})`}
                      disabled={true}
                    />
                  </FormField>
                  <FormField
                    label='Approval needed by'
                    constraintText='Use YYYY/MM/DD format. Date must be after today.'
                  >
                    <DatePicker
                      onChange={async (e) => {
                        let newAdditionalMetadata = JSON.parse(
                          JSON.stringify(props.additionalMetadata),
                        ) as TemplateMetadata[];
                        newAdditionalMetadata.forEach((aMISToCompare) => {
                          const amiApprovalIdToCompare =
                            aMISToCompare.accessManagementInfo?.approvalsWorkflowTemplate?.approvalTemplateId;
                          const amiApprovalId =
                            aMIS.accessManagementInfo?.approvalsWorkflowTemplate?.approvalTemplateId;
                          const arwApprovalIdToCompare =
                            aMISToCompare.accessRequestWorkflows?.[0]?.approvalsWorkflowTemplate?.approvalTemplateId;
                          const arwApprovalId =
                            aMIS.accessRequestWorkflows?.[0]?.approvalsWorkflowTemplate?.approvalTemplateId;
                          if (!!amiApprovalIdToCompare && !!amiApprovalId && amiApprovalIdToCompare == amiApprovalId) {
                            aMISToCompare.accessManagementInfo.approvalsWorkflowTemplate.neededOnDate = e.detail.value;
                          }
                          if (!!arwApprovalIdToCompare && !!arwApprovalId && arwApprovalIdToCompare == arwApprovalId) {
                            aMISToCompare.accessRequestWorkflows[0].approvalsWorkflowTemplate.neededOnDate =
                              e.detail.value;
                          }
                        });
                        props.setAdditionalMetadata(newAdditionalMetadata);
                      }}
                      value={
                        aMIS?.accessManagementInfo?.approvalsWorkflowTemplate?.neededOnDate ??
                        aMIS.accessRequestWorkflows?.[0]?.approvalsWorkflowTemplate.neededOnDate
                      }
                      openCalendarAriaLabel={(selectedDate) =>
                        'Choose approval neededOnDate' + (selectedDate ? `, selected date is ${selectedDate}` : '')
                      }
                      invalid={
                        !validateNeededOnDate(
                          aMIS?.accessManagementInfo?.approvalsWorkflowTemplate?.neededOnDate ??
                            aMIS.accessRequestWorkflows?.[0]?.approvalsWorkflowTemplate.neededOnDate,
                        )
                      }
                      isDateEnabled={(date) => date > new Date()}
                      nextMonthAriaLabel='Next month'
                      placeholder='YYYY/MM/DD'
                      previousMonthAriaLabel='Previous month'
                      todayAriaLabel='Today'
                    />
                  </FormField>
                  <FormField label='Approval priority' description='Select the approval priority.'>
                    <Select
                      selectedOption={{
                        label: convertValueToLabel(
                          aMIS?.accessManagementInfo?.approvalsWorkflowTemplate?.priority ??
                            aMIS.accessRequestWorkflows?.[0]?.approvalsWorkflowTemplate.priority,
                        ),
                        value:
                          aMIS?.accessManagementInfo?.approvalsWorkflowTemplate?.priority ??
                          aMIS.accessRequestWorkflows?.[0]?.approvalsWorkflowTemplate.priority,
                      }}
                      onChange={(e) => {
                        let newAdditionalMetadata = JSON.parse(
                          JSON.stringify(props.additionalMetadata),
                        ) as TemplateMetadata[];
                        newAdditionalMetadata.forEach((aMISToCompare) => {
                          const amiApprovalIdToCompare =
                            aMISToCompare.accessManagementInfo?.approvalsWorkflowTemplate?.approvalTemplateId;
                          const amiApprovalId =
                            aMIS.accessManagementInfo?.approvalsWorkflowTemplate?.approvalTemplateId;
                          const arwApprovalIdToCompare =
                            aMISToCompare.accessRequestWorkflows?.[0]?.approvalsWorkflowTemplate?.approvalTemplateId;
                          const arwApprovalId =
                            aMIS.accessRequestWorkflows?.[0]?.approvalsWorkflowTemplate?.approvalTemplateId;
                          if (!!amiApprovalIdToCompare && !!amiApprovalId && amiApprovalIdToCompare == amiApprovalId) {
                            aMISToCompare.accessManagementInfo.approvalsWorkflowTemplate.priority =
                              e.detail.selectedOption.value;
                          }
                          if (!!arwApprovalIdToCompare && !!arwApprovalId && arwApprovalIdToCompare == arwApprovalId) {
                            aMISToCompare.accessRequestWorkflows[0].approvalsWorkflowTemplate.priority =
                              e.detail.selectedOption.value;
                          }
                        });
                        props.setAdditionalMetadata(newAdditionalMetadata);
                      }}
                      options={[
                        { label: 'Low', value: 'LOW' },
                        { label: 'Medium', value: 'MEDIUM' },
                        { label: 'High', value: 'HIGH' },
                      ]}
                      selectedAriaLabel='Selected'
                    />
                  </FormField>
                </SpaceBetween>
              </ExpandableSection>
            );
          })}
        </Container>
      )}
    </SpaceBetween>
  );
};
